/**
 * One line for where an account came from, from its touch.
 *
 * A touch records what the arrival actually knew, so most fields are absent
 * most of the time; the line is composed from what is there, most specific
 * first — a campaign says more than a referrer, a referrer more than a
 * property. Nothing is invented: an account that pre-dates capture has no
 * touch, and says so.
 */
import type { AcquisitionTouch } from '../types';

/** The headline — what a list column shows. */
export function acquisitionSource(t: AcquisitionTouch | undefined): string {
  if (!t) return '';
  if (t.source === 'provisioned') return t.provisionedAs ?? 'provisioned';
  if (t.link) return `link · ${t.link.slug}`;
  if (t.source) return t.source;
  if (t.referrerHost) return t.referrerHost;
  return t.property === 'zitmus' ? 'zitmus · direct' : 'direct';
}

/** The whole story — what a detail page shows. */
export function describeAcquisition(t: AcquisitionTouch | undefined): string {
  if (!t) return 'not recorded';
  const parts: string[] = [];
  if (t.source === 'provisioned') {
    parts.push('provisioned', t.provisionedAs ?? '');
  } else {
    if (t.property) parts.push(t.property);
    if (t.link) {
      const who = t.link.publisher.kind === 'partner' ? `partner ${t.link.publisher.id}`
        : t.link.publisher.kind === 'student' ? 'a student’s link' : 'our link';
      parts.push(`link ${t.link.slug} (${who})`);
    } else if (t.landingPath) {
      parts.push(t.landingPath);
    }
    if (t.referrerHost) parts.push(`from ${t.referrerHost}`);
    const tags = [t.source, t.medium, t.campaign, t.variant].filter(Boolean).join(' / ');
    if (tags) parts.push(tags);
    if (t.placement) parts.push(`via ${t.placement}`);
    if (t.click) parts.push(`${t.click.param} click`);
  }
  return parts.filter(Boolean).join(' · ');
}
